/**
 * Country list for the quote form.
 *
 * Country is a PRICING INPUT, not something printed on the quote: `deviceShippings`
 * declares `internal.country: ["us"]` and `international.country: null`, so the only thing
 * the value decides is US-domestic vs international shipping. It was a free-text field,
 * which meant a typo ("Untied States") silently produced international rates.
 *
 * Codes are ISO 3166-1 alpha-2; the LABELS come from `Intl.DisplayNames` rather than being
 * hardcoded, so we carry a list of codes instead of 250 English strings that would drift.
 */

/** ISO 3166-1 alpha-2, officially assigned. */
const ISO_ALPHA2 = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO ' +
  'BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ ' +
  'DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP ' +
  'GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG ' +
  'KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML ' +
  'MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE ' +
  'PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL ' +
  'SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM ' +
  'US UY UZ VA VC VE VG VI VN VU WF WS YE ZA ZM ZW'
).split(' ');

export interface CountryOption {
  /** ISO alpha-2, what the form stores. */
  code: string;
  label: string;
}

/**
 * `Intl.DisplayNames` is available in every browser we support and in Node's full-ICU
 * build, but fall back to the bare code rather than crashing the page if a runtime lacks
 * it — a missing country label must not cost a rep their quote.
 */
function displayName(code: string): string {
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    return dn.of(code) ?? code;
  } catch {
    return code;
  }
}

let cached: CountryOption[] | null = null;

/** Alphabetical by label, with the US first — it is by far the common case. */
export function countryOptions(): CountryOption[] {
  if (cached) return cached;
  const dn = (() => {
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      return null;
    }
  })();
  const all = ISO_ALPHA2.map((code) => ({ code, label: dn?.of(code) ?? code }));
  all.sort((a, b) => a.label.localeCompare(b.label));
  const us = all.filter((c) => c.code === 'US');
  cached = [...us, ...all.filter((c) => c.code !== 'US')];
  return cached;
}

/**
 * Resolve whatever a deep link supplied to an ISO code, so the dropdown can display it.
 *
 * Accepts a code ("US", "gb"), an English country name ("United States", "United
 * Kingdom"), or the loose spellings `inferRegion` already tolerates ("usa", "america").
 * Returns the input unchanged when it resolves to nothing — the engine infers the region
 * from the raw string either way, so an unrecognised country still prices, it just cannot
 * be shown as a selected option.
 */
export function normalizeCountry(input: string | undefined): string | undefined {
  const raw = (input ?? '').trim();
  if (!raw) return undefined;

  const upper = raw.toUpperCase();
  if (upper.length === 2 && ISO_ALPHA2.includes(upper)) return upper;

  const squashed = raw.toLowerCase().replace(/[^a-z]/g, '');
  // The spellings inferRegion accepts, so a link written for the engine also selects here.
  if (['usa', 'unitedstates', 'unitedstatesofamerica', 'america'].includes(squashed)) return 'US';
  if (['uk', 'greatbritain', 'britain'].includes(squashed)) return 'GB';

  for (const { code, label } of countryOptions()) {
    if (label.toLowerCase().replace(/[^a-z]/g, '') === squashed) return code;
  }
  return raw;
}

/** True when the value maps to a real option, so the Select can show it. */
export function isKnownCountry(value: string | undefined): boolean {
  return !!value && ISO_ALPHA2.includes(value.toUpperCase());
}

export { displayName as countryLabel };
